import { QueryClient } from '@tanstack/react-query';
import { invalidateApiCache } from '../utils/swCacheUtils';
import { DEFAULT_CACHE_POLICY, getStaleTimeForResource, onPolicyChange } from '../utils/cachePolicyManager';

// Create QueryClient with default options
// These will be overridden dynamically for specific resources
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Start with paged records TTL (most common, least stale tolerance)
      staleTime: DEFAULT_CACHE_POLICY.paged_records_ttl_seconds * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false, // Disable auto-refetch on window focus
      refetchOnReconnect: false,   // Disable auto-refetch on reconnect
      refetchOnMount: false,       // Disable auto-refetch on mount
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * (2 ** attempt), 5000),
    },
    mutations: {
      retry: 0,
    },
  },
});

// Setup mutation cache observer to invalidate SW cache on mutations
queryClient.getMutationCache().subscribe((event) => {
  if (event.type === 'success' && event.mutation.options?.invalidateEndpoint) {
    const endpoint = event.mutation.options.invalidateEndpoint;
    console.log(`Invalidating cache for endpoint: ${endpoint}`);
    invalidateApiCache(endpoint).catch((err) =>
      console.warn('Failed to invalidate cache:', err)
    );
  }
});

/**
 * Update queryClient default staleTime when cache policy changes
 * Called on app startup and whenever admin updates TTL settings
 */
export const syncQueryClientWithCachePolicy = () => {
  onPolicyChange((policy) => {
    // Update default options with new paged records TTL
    // This affects all future queries that don't specify custom staleTime
    queryClient.setDefaultOptions({
      queries: {
        staleTime: (policy.paged_records_ttl_seconds || DEFAULT_CACHE_POLICY.paged_records_ttl_seconds) * 1000,
      },
    });
    
    console.log('[QueryClient] Synced with cache policy, staleTime:', 
      (policy.paged_records_ttl_seconds || DEFAULT_CACHE_POLICY.paged_records_ttl_seconds) + 's');
  });
};

/**
 * Create a useQuery call with custom staleTime per resource type
 * Import and use this in custom hooks
 * @param {string} resourceType - Type of resource (e.g., 'patients', 'dashboard', 'branches')
 * @returns {number} staleTime in milliseconds
 */
export const getQueryStaleTime = (resourceType) => {
  return getStaleTimeForResource(resourceType);
};
